import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seo from './pages';

const { SITE_URL, DEFAULT_TITLE, DEFAULT_DESCRIPTION, pageForPath, isNoindexPath } = seo;

/**
 * Keep the document head in step with the current route.
 *
 * scripts/prerender.mjs writes these tags into the HTML each public page is
 * served as, which is what a crawler reads on a cold visit. This covers the other
 * half: once React is running, moving between pages never fetches HTML again, so
 * without this the title would stay on whichever page was opened first. Both read
 * their wording from src/seo/pages.js, so the served title and the rendered one
 * cannot disagree - a mismatch is what Google treats as cloaking.
 *
 * App routes get a generic title and noindex, so a client-side visit to /billing
 * never keeps the last marketing title or canonical.
 */
export default function useSeoHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = pageForPath(pathname);
    const title = page ? page.title : DEFAULT_TITLE;
    const description = page ? page.description : DEFAULT_DESCRIPTION;

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);

    if (page) {
      const canonicalPath = pathname !== '/' && pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : pathname;
      const url = `${SITE_URL}${canonicalPath}`;
      setLink('canonical', url);
      setMeta('property', 'og:url', url);
      removeTag('meta[name="robots"]');
    } else {
      removeTag('link[rel="canonical"]');
      removeTag('meta[property="og:url"]');
      // Everything that is not an indexable page is kept out of the index. The
      // app screens need a session and have nothing to offer search.
      if (isNoindexPath(pathname)) {
        setMeta('name', 'robots', 'noindex');
      } else {
        removeTag('meta[name="robots"]');
      }
    }
  }, [pathname]);
}

function setMeta(attribute, name, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

function removeTag(selector) {
  document.head.querySelector(selector)?.remove();
}
